import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import { paymentMethod } from '@ourbranch/lookups';

import { useStore } from 'core/store';
import { FormField } from 'core/components/form';
import { TooltipWrapper } from 'core/components/tooltip-wrapper';
import { Label } from 'core/components/label';
import ValueField from 'core/components/value-field';
import RemoveButton from 'core/components/remove-button';
import { setPrimaryMortgageValues, trackCustomMortgageLender } from 'offer/helpers/mortgage-helpers';
import { IncorrectBillingWarning } from '../incorrect-billing-warning';
import MortgageRadio from './mortgage-radio';
import useStyles from './mortgage-item.styles';

const MortgageItem = ({ fieldId: id, fieldName, index, onRemove, item, removeFromList }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { home, condo, farm } = values;

  let mortgageDetails;
  if (home) {
    mortgageDetails = home.mortgageDetails;
  } else if (condo) {
    mortgageDetails = condo.mortgageDetails;
  } else if (farm) {
    mortgageDetails = farm.mortgageDetails;
  }

  const {
    account: {
      policies: { policy: policyStore }
    },
    offer
  } = useStore();

  const { policy } = policyStore;
  const mortgage = mortgageDetails[index];

  useEffect(() => {
    if (mortgage.primary) {
      setPrimaryMortgageValues(setFieldValue, mortgage);
    }
  }, [mortgage, setFieldValue]);

  const handleRemove = useCallback(() => {
    if (mortgage.isCustom) {
      const idToTrack = policy?.id ? { policyId: policy?.id } : { offerId: offer?.offer?.id };
      trackCustomMortgageLender(false, idToTrack, mortgage);
    }
    if (mortgage.primary) {
      setPrimaryMortgageValues(setFieldValue, {
        mortgageHolderAddress: {
          city: '',
          state: '',
          zip: '',
          address: '',
          address2: ''
        },
        mortgageHolderEmail: '',
        mortgageHolderFax: '',
        mortgageHolderName: '',
        mortgageHolderId: '',
        loanNumber: ''
      });
    }
    onRemove(() => {
      removeFromList();
      return index;
    });
  }, [setFieldValue, onRemove, index, removeFromList, mortgage, offer?.offer?.id, policy?.id]);

  const onRadioChange = useCallback(() => {
    const previousPrimaryIndex = mortgageDetails.findIndex((mgItem) => mgItem.primary);
    if (previousPrimaryIndex >= 0) {
      setFieldValue(`${id}.${previousPrimaryIndex}.primary`, false);
    }

    setFieldValue(`${fieldName}.primary`, true);
    setFieldTouched(id);
  }, [mortgageDetails, setFieldValue, id, fieldName, setFieldTouched]);

  const preventRemoveMortgage = useMemo(() => {
    // can't remove mortgage if payment type is mortgage with only one mortgage
    return (
      policy?.paymentMethod === paymentMethod.Escrow && mortgageDetails[index].primary && mortgageDetails.length <= 1
    );
  }, [policy?.paymentMethod, mortgageDetails, index]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.identifier}>
          <Label type="darkSmall">MORTGAGE #{mortgageDetails[index].loanNumber}</Label>
          {preventRemoveMortgage && (
            <Label type="infoLabel">Change the payment method, or add a mortgage to eliminate this mortgage.</Label>
          )}
        </div>
        <div>
          <MortgageRadio checked={mortgageDetails[index].primary || false} onChange={onRadioChange} classes={classes} />
          <RemoveButton
            mode="big"
            actionType="button"
            className={classes.button}
            disabled={preventRemoveMortgage}
            onClick={handleRemove}
          />
        </div>
      </div>
      <Grid container xs={12} spacing={4}>
        <Grid item xs={6}>
          <ValueField label="Lender Name" value={item.mortgageHolderName} />
        </Grid>
        <Grid item xs={6}>
          <ValueField
            label="Billing Address"
            value={`${item.mortgageHolderAddress?.address} ${item.mortgageHolderAddress?.city}, ${item.mortgageHolderAddress?.state}, ${item.mortgageHolderAddress?.zip}`}
          />
        </Grid>
        {item.isCustom && (
          <>
            <IncorrectBillingWarning />
            <TooltipWrapper
              xs={6}
              tooltip={{
                text: "Adding the lender's email can streamline billing processing by providing a backup method for payment collection. While optional, it is highly recommended."
              }}
            >
              <FormField
                mode="light"
                name={`${fieldName}.mortgageHolderEmail`}
                id={`${fieldName}.mortgageHolderEmail`}
                label="Email (optional)"
                type="string"
                permissions={{ isLicensedAction: false }}
              />
            </TooltipWrapper>
            <TooltipWrapper
              xs={6}
              tooltip={{
                text: "Adding the lender's fax number can streamline billing processing by providing a backup method for payment collection. While optional, it is highly recommended."
              }}
            >
              <FormField
                mode="light"
                name={`${fieldName}.mortgageHolderFax`}
                id={`${fieldName}.mortgageHolderFax`}
                label="Fax (optional)"
                type="string"
                permissions={{ isLicensedAction: false }}
              />
            </TooltipWrapper>
          </>
        )}
        <FormField
          name={`${fieldName}.loanNumber`}
          id={`${fieldName}.loanNumber`}
          type="string"
          label="Loan Number"
          mode="light"
          xs={12}
          permissions={{ isLicensedAction: false }}
        />
      </Grid>
    </div>
  );
};

MortgageItem.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  removeFromList: PropTypes.func.isRequired
};

export default observer(MortgageItem);
